import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import Label from "../../../components/Label";
import { splitDate } from "../../../utils/tableFunctions";
import { Clear, Edit } from "@mui/icons-material";
import TableView from "../../../components/Table";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { styles } from "../../../utils/styles";
import { APP_USER_SESSION } from "../../../constant/AppConstants";
import { StorageUtils } from "../../../utils";
import EditDialog from "../../../components/Admin-Terminal/Dialogs/EditDialog";
import { getPaymentMethodDetails, getPaymentMethodWithActions } from "../../../services/payment";
import PendingDetailsComparison from "../../PendingDetailsComparison";

const PAYMENT_METHOD_FIELDS = [
  { key: "name", label: "Payment Method" },
];

const PaymentMethodDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [paymentMethodDetails, setPaymentMethodDetails] = useState();
  const [loaded, setLoaded] = useState();
  const [selectedId, setSelectedId] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION)?.role || {};
  const userRoleTerminal = userRole?.terminal || [];

  useEffect(() => {
    if (!detailsData && !action) {
      getPaymentMethodDetails(id, setPaymentMethodDetails, setLoaded);
      return;
    }
    if (!detailsData && action) {
      getPaymentMethodWithActions(id, setPaymentMethodDetails, setLoaded);
      return;
    }

    setPaymentMethodDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const handleCloseDialog = () => {
    selectedId && setSelectedId();
  };

  const formatData = (input) => {
    let tableData = [];
    input.results.forEach(({ id, is_active, mode, terminal_id, paymentMethod }) => {
      tableData.push({
        id: id,
        terminal_id: terminal_id,
        mode: mode && mode.mode,
        paymentMethod: paymentMethod && paymentMethod.name,
        is_active: is_active ? "Active" : "Inactive",
        action: (
          <>
            {
              is_active && userRoleTerminal.includes("Edit") &&
              <Tooltip title="Edit" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(id);
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            }
          </>
        )
      });
    });
    return tableData;
  };

  // const getNext = async () => {
  //   if (!next) return;
  //   const res = await getNextPage({
  //     url: next,
  //     filter: filter,
  //   });
  //   let newRows = formatData(res);
  //   setTableData((old) => [...old, ...newRows]);
  //   setNext(res.next ? res.next : false);
  // };

  const InfoColumn = () => {
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">PaymentMethod</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentMethodDetails.name?.toUpperCase()}
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Deleted"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={paymentMethodDetails.is_approved ? "success" : "error"}
                >
                  {paymentMethodDetails.is_approved ? "YES" : "NO"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={paymentMethodDetails.is_active ? "success" : "error"}
                >
                  {paymentMethodDetails.is_active ? "NO" : "YES"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentMethodDetails.created_at
                ? splitDate(paymentMethodDetails.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentMethodDetails.created_by
                ? paymentMethodDetails.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentMethodDetails.approved_at
                ? splitDate(paymentMethodDetails.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {paymentMethodDetails.approved_by
                ? paymentMethodDetails.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {splitDate(paymentMethodDetails.updated_at)}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {paymentMethodDetails.updated_by
                    ? paymentMethodDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="PaymentMethod Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {paymentMethodDetails.action && paymentMethodDetails.action}
        </Typography>
      )}
      {loaded ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
              {isCheckerView && !paymentMethodDetails.original_unavailable ? (
                <>
                  <PendingDetailsComparison
                    data={paymentMethodDetails}
                    original={paymentMethodDetails.original}
                    fields={PAYMENT_METHOD_FIELDS}
                  />
                  <DateColumn />
                </>
              ) : (
                <>
                  {action && <DateColumn />}
                  <InfoColumn />
                  {!action && <DateColumn />}
                </>
              )}
            </Grid>
            {paymentMethodDetails.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {paymentMethodDetails.reject_message}
                </Typography>
              </Typography>
            )}
            {paymentMethodDetails.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {paymentMethodDetails.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {selectedId &&
        <EditDialog
          open={true}
          onClose={handleCloseDialog}
          selectedId={selectedId}
        />
      }
    </Container>
  );
};

export default PaymentMethodDetails;
